import React, { FC, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { PsModalTuner } from 'stencilSetup';

declare const bootstrap: any;

export interface OrbitModalProps {
  modalId: string;
  title: string;
  isOpen: boolean;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  extraClass?: string;
  primaryActive?: boolean;
  modalStatic?: boolean;
  modalKeyboard?: boolean;
  keepMounted?: boolean;
  awaitSuccess?: boolean;
  handleSubmit?: () => void;
  handleClose?: () => void;
  disablePrimary?: boolean;
  disablePrimaryDismiss?: boolean;
  children?: React.ReactNode;
}

export const OrbitModal: FC<OrbitModalProps> = ({
  modalId,
  title,
  isOpen,
  primaryButtonLabel = 'Submit',
  secondaryButtonLabel = 'Cancel',
  extraClass,
  primaryActive = true,
  modalStatic = false,
  modalKeyboard = true,
  keepMounted = false,
  // eslint-disable-next-line
  awaitSuccess = false,
  handleSubmit,
  handleClose,
  disablePrimary = false,
  disablePrimaryDismiss = false,
  children,
}) => {
  const [isDelayedOpen, setIsDelayedOpen] = useState(false);

  // Add logging for component mount/unmount and cleanup
  useEffect(() => {
    return () => {
      // Clean up any remaining modal backdrop
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }
      // Also remove modal-open class from body if no other modals are open
      if (!document.querySelector('.modal.show')) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [modalId]);

  const handleSubmitModal = () => {
    if (handleSubmit) {
      closeModal(handleSubmit);
    }
  };

  const handleCloseModal = () => {
    if (handleClose) {
      closeModal(handleClose);
    }
  };

  const closeModal = useCallback((callback: () => void) => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', callback, { once: true });
    }
  }, [modalId]);

  const openModal = useCallback(() => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modalInstance = bootstrap.Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.show();
      } else {
        const newModalInstance = new bootstrap.Modal(modalElement, {
          backdrop: modalStatic ? 'static' : true,
          keyboard: modalKeyboard ? true : false,
        });
        newModalInstance.show();
      }
    }
  }, [modalId, modalKeyboard, modalStatic]);

  useEffect(() => {
    if (isOpen) {
      const loadTimer = setTimeout(() => {
        if (keepMounted) {
          openModal();
          // Add handler for backdrop/keyboard close using existing closeModal method
          const modalElement = document.getElementById(modalId);
          if (modalElement) {
            modalElement.addEventListener('hide.bs.modal', (event: any) => {
              if (event.target === modalElement && handleClose) {
                closeModal(handleClose);
              }
            });
          }
        } else {
          setIsDelayedOpen(true);

          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.type === 'childList') {
                const modalElement = document.getElementById(modalId);
                if (modalElement) {
                  openModal();
                  observer.disconnect();
                  break;
                }
              }
            }
          });

          observer.observe(document.body, { childList: true, subtree: true });
          return () => observer.disconnect();
        }
      }, 25);
      return () => clearTimeout(loadTimer);
    } else if (!keepMounted) {
      setIsDelayedOpen(false);
    }
  }, [modalId, isOpen, keepMounted, openModal, handleClose, closeModal]);

  return isDelayedOpen || keepMounted
    ? createPortal(
        <PsModalTuner
          modalId={modalId}
          modalTitle={title}
          primaryActive={primaryActive}
          primaryButton={primaryButtonLabel}
          secondaryButton={secondaryButtonLabel}
          extraClass={extraClass}
          disablePrimary={disablePrimary}
          disablePrimaryDismiss={disablePrimaryDismiss}
          onPsModalSubmit={handleSubmitModal}
          onPsModalDismiss={handleCloseModal}
          modalStatic={modalStatic}
        >
          {children}
        </PsModalTuner>,
        document.body
      )
    : null;
};
